import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    DatePicker,
    Checkbox,
    Button,
    Row,
    Col,
    Typography,
    Space,
    Spin,
    message,
    Radio,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";

const { Title } = Typography;
const { TextArea } = Input;

// 固定ドキュメントID（好きな名前に変えてOK）
const COLLECTION_NAME = "notification";
const DOC_ID = "main"; // ← only one document

const Notification = (props) => {
    const { db, history } = props;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);

    // ===============================
    // Load single notification doc
    // ===============================
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const docRef = db.collection(COLLECTION_NAME).doc(DOC_ID);
                const snap = await docRef.get();

                if (snap.exists) {
                    const data = snap.data();

                    form.setFieldsValue({
                        buttonTitle: data.buttonTitle || "",
                        title: data.title || "",
                        body: data.body || "",
                        displayStart: data.displayStart
                            ? dayjs(data.displayStart.toDate())
                            : null,
                        displayEnd: data.displayEnd
                            ? dayjs(data.displayEnd.toDate())
                            : null,
                        isActive: !!data.isActive,
                        userType: data.userType || "all",
                        targets: data.targets || [],
                    });
                } else {
                    // 初回は何も入ってない状態
                    // message.info("お知らせはまだ登録されていません。");
                }
            } catch (err) {
                console.error(err);
                message.error("お知らせの読み込み中にエラーが発生しました。");
            } finally {
                setLoading(false);
                setInitialLoaded(true);
            }
        };

        loadData();
    }, [db, form]);

    // ===============================
    // Save (always same doc id)
    // ===============================
    const handleFinish = async (values) => {
        try {
            setLoading(true);

            const payload = {
                buttonTitle: values.buttonTitle || "",
                title: values.title || "",
                body: values.body || "",
                userType: values.userType || "all",
                displayStart: values.displayStart
                    ? values.displayStart.toDate()
                    : null,
                displayEnd: values.displayEnd ? values.displayEnd.toDate() : null,
                isActive: !!values.isActive,
                targets: values.targets || [],
                updatedAt: new Date(),
            };

            const docRef = db.collection(COLLECTION_NAME).doc(DOC_ID);

            await docRef.set(
                {
                    ...payload,
                    // createdAt は初回だけ残したいなら merge + check が必要だけど
                    // ここでは毎回 updatedAt だけ更新でもOK
                },
                { merge: true }
            );

            message.success("お知らせを保存しました。");
            if (history && history.goBack) {
                history.goBack();
            }
        } catch (err) {
            console.error(err);
            message.error("保存中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (history && history.goBack) {
            history.goBack();
        }
    };

    if (props.user && !props.user.admin) {
        return (<div>permission required</div>)
    }

    if (!initialLoaded) {
        return (
            <div
                style={{
                    maxWidth: 900,
                    margin: "80px auto",
                    textAlign: "center",
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: 900,
                margin: "40px auto",
                padding: "32px 40px 40px",
                background: "#ffffff",
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
        >
            <Title level={3} style={{ marginBottom: 32 }}>
                お知らせ編集画面
            </Title>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                requiredMark={false}
            >
                <Form.Item label="ボタンタイトル" name="buttonTitle">
                    <Input />
                </Form.Item>

                <Form.Item label="タイトル" name="title">
                    <Input />
                </Form.Item>

                <Form.Item label="本文" name="body">
                    <TextArea
                        rows={8}
                        placeholder=""
                    />
                </Form.Item>

                {/* 表示期間 */}
                <Form.Item label="表示期間" style={{ marginBottom: 24 }}>
                    <Row gutter={16} align="middle">
                        <Col span={11}>
                            <Form.Item name="displayStart" noStyle>
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col
                            span={2}
                            style={{
                                textAlign: "center",
                                lineHeight: "32px",
                            }}
                        >
                            〜
                        </Col>
                        <Col span={11}>
                            <Form.Item name="displayEnd" noStyle>
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form.Item>

                {/* 対象ユーザー選択 */}
                <Form.Item
                    label="対象ユーザー"
                    name="userType"
                    initialValue="all"
                    style={{ marginBottom: 24 }}
                >
                    <Radio.Group>
                        <Radio value="seller">販売側</Radio>
                        <Radio value="buyer">購入側</Radio>
                        <Radio value="all">全ユーザー</Radio>
                    </Radio.Group>
                </Form.Item>

                {/* 公開状態 */}
                <Form.Item
                    label="公開状態"
                    name="isActive"
                    valuePropName="checked"
                    style={{ marginBottom: 16 }}
                >
                    <Checkbox>有効</Checkbox>
                </Form.Item>

                {/* Buttons */}
                <Form.Item style={{ marginTop: 40, textAlign: "center" }}>
                    <Space size={40}>
                        <Button
                            style={{ minWidth: 160 }}
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            キャンセル
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{ minWidth: 160 }}
                            loading={loading}
                        >
                            保存
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </div>
    );
};

export default Notification;
